/**
 * Masquerade Banner Component
 *
 * Displays a prominent banner when:
 * 1. A mentor is viewing a client's data (masquerade mode)
 * 2. A client is viewing mentor-created content (client view mode)
 *
 * Provides a quick way to exit the current mode and return to personal view.
 */

import React, { useEffect } from "react";
import { useScope } from "../../hooks/useScope";
import { useHistory } from "react-router-dom";
import { useAnalytics } from "../../hooks/useAnalytics";
import { useTranslation } from "../../hooks/useTranslation";
import "./MasqueradeBanner.css";

export const MasqueradeBanner = () => {
  const { isManaged, isClientView, clientName, exitManagedScope } = useScope();
  const history = useHistory();
  const analytics = useAnalytics();
  const t = useTranslation();

  // Add/remove body class for border overlay
  // Apply to both mentor masquerade mode and client view mode
  const isMentorMasquerade = isManaged && !isClientView;

  useEffect(() => {
    // Apply border for both mentor masquerade and client view modes
    if (isManaged) {
      document.body.classList.add("masquerade-mode");
    } else {
      document.body.classList.remove("masquerade-mode");
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove("masquerade-mode");
    };
  }, [isManaged]);

  if (!isManaged) {
    return null;
  }

  const handleExit = () => {
    // Track mode exit
    analytics.trackCTAClick({
      section: "masquerade_banner",
      action: isClientView ? "client_view_exit" : "masquerade_exit",
      source: "exit_button",
    });

    exitManagedScope();

    // Redirect based on mode
    if (isClientView) {
      history.push("/history"); // Client goes to stored plans in personal view
    } else {
      history.push("/mentor/clients"); // Mentor goes back to client management
    }
  };

  // Different display for mentor masquerade vs client view
  const bannerIcon = isClientView ? "👀" : "👁️";
  const bannerText = isClientView
    ? t("client.viewSwitcher.mentorView", "Mentor's View")
    : `${t("mentor.masqueradeBanner.viewing", "Viewing Client:")} `;
  const exitText = isClientView
    ? t("client.viewSwitcher.personal", "Personal View")
    : t("mentor.masqueradeBanner.exit", "Exit Client View");

  return (
    <div
      className={`masquerade-banner ${isClientView ? "client-view-mode" : ""}`}
    >
      <div className="masquerade-content">
        <div className="masquerade-info">
          <span className="masquerade-icon">{bannerIcon}</span>
          <span className="masquerade-text">
            {isClientView ? (
              <strong>{bannerText}</strong>
            ) : (
              <>
                {bannerText}
                <strong>{clientName || "Client"}</strong>
              </>
            )}
          </span>
        </div>
        <button className="masquerade-exit-btn" onClick={handleExit}>
          ← {exitText}
        </button>
      </div>
    </div>
  );
};

export default MasqueradeBanner;
